import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Camera, RotateCcw, AlertCircle, Loader } from 'lucide-react';

/**
 * LivePhotoCapture
 *
 * Captures a photo directly from the device camera at the moment of signing —
 * intentionally does NOT support choosing an existing image from the gallery, since
 * the point is to verify the signer was actually present when they signed.
 *
 * Props:
 * - onCapture: function(base64ImageDataUrl) - called when the user confirms a photo
 * - onClear: function() - called if a confirmed photo is retaken/cleared
 * - label: string - shown above the camera view
 */
const LivePhotoCapture = ({ onCapture = null, onClear = null, label = 'Live Photo' }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const [status, setStatus] = useState('idle'); // idle | starting | live | error
  const [errorMsg, setErrorMsg] = useState('');
  const [capturedImage, setCapturedImage] = useState(null);

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  }, []);

  const startCamera = useCallback(async () => {
    setStatus('starting');
    setErrorMsg('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setStatus('live');
    } catch (err) {
      console.error('Camera access failed:', err);
      let msg = 'Could not access your camera.';
      if (err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError') {
        msg = 'Camera access was denied. Please allow camera permission and try again — a live photo is required to sign.';
      } else if (err?.name === 'NotFoundError') {
        msg = 'No camera was found on this device. A live photo is required to sign this agreement.';
      }
      setErrorMsg(msg);
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    startCamera();
    return () => stopStream();
  }, [startCamera, stopStream]);

  const handleCapture = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    // Mirror the frame so the captured photo matches what the user saw in the preview
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
    setCapturedImage(dataUrl);
    stopStream();
    setStatus('idle');
    onCapture?.(dataUrl);
  };

  const handleRetake = () => {
    setCapturedImage(null);
    onClear?.();
    startCamera();
  };

  return (
    <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
      <p className="text-sm text-gray-400 mb-3">{label}</p>

      {capturedImage ? (
        <div className="space-y-3">
          <div className="bg-black rounded overflow-hidden flex justify-center">
            <img src={capturedImage} alt="Captured" className="max-h-48 object-contain" />
          </div>
          <button
            type="button"
            onClick={handleRetake}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm font-medium transition"
          >
            <RotateCcw className="w-4 h-4" /> Retake Photo
          </button>
        </div>
      ) : status === 'error' ? (
        <div className="bg-red-900/30 border border-red-700 rounded-lg p-4 flex gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-red-200 mb-3">{errorMsg}</p>
            <button
              type="button"
              onClick={startCamera}
              className="px-3 py-1.5 bg-red-800 hover:bg-red-700 text-white rounded text-xs font-medium transition"
            >
              Try Again
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="bg-black rounded overflow-hidden flex items-center justify-center" style={{ minHeight: '180px' }}>
            {status === 'starting' && (
              <div className="flex flex-col items-center gap-2 text-gray-400 py-8">
                <Loader className="w-6 h-6 animate-spin" />
                <span className="text-xs">Starting camera…</span>
              </div>
            )}
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{ transform: 'scaleX(-1)', display: status === 'live' ? 'block' : 'none', maxHeight: '240px' }}
              className="w-full object-contain"
            />
          </div>
          <button
            type="button"
            onClick={handleCapture}
            disabled={status !== 'live'}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Camera className="w-4 h-4" /> Capture Photo
          </button>
        </div>
      )}

      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </div>
  );
};

export default LivePhotoCapture;
